"use client";

import { c as _c } from "react/compiler-runtime";
import type { Variants } from "framer-motion";
import type { HTMLAttributes } from "react";
import { forwardRef, useCallback, useImperativeHandle, useRef } from "react";
import { m, useAnimation } from "framer-motion";
import { cn } from "@/lib/utils";
export interface SparklesIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}
interface SparklesIconProps extends HTMLAttributes<HTMLDivElement> {
  size?: number;
}

// Container slight bounce
const SVG_VARIANTS: Variants = {
  normal: {
    scale: 1
  },
  animate: {
    scale: [1, 1.1, 1],
    transition: {
      duration: 0.5,
      ease: "easeInOut"
    }
  }
};

// Main large sparkle (center-right)
const SPARKLE_MAIN_VARIANTS: Variants = {
  normal: {
    opacity: 1,
    scale: 1,
    rotate: 0
  },
  animate: {
    opacity: [0.5, 1, 0.5, 1],
    scale: [0.8, 1.2, 0.9, 1],
    rotate: [0, 15, -5, 0],
    transition: {
      duration: 0.6,
      ease: "easeInOut"
    }
  }
};

// Small sparkle (top-left) - delayed
const SPARKLE_SMALL1_VARIANTS: Variants = {
  normal: {
    opacity: 1,
    scale: 1
  },
  animate: {
    opacity: [0.3, 1, 0.3, 1],
    scale: [0.6, 1.3, 0.8, 1],
    transition: {
      duration: 0.5,
      delay: 0.1,
      ease: "easeInOut"
    }
  }
};

// Small sparkle (bottom-left) - more delayed
const SPARKLE_SMALL2_VARIANTS: Variants = {
  normal: {
    opacity: 1,
    scale: 1
  },
  animate: {
    opacity: [0.3, 1, 0.3, 1],
    scale: [0.6, 1.4, 0.7, 1],
    transition: {
      duration: 0.5,
      delay: 0.2,
      ease: "easeInOut"
    }
  }
};
const SparklesIcon = forwardRef<SparklesIconHandle, SparklesIconProps>((t0, ref) => {
  const $ = _c(39);
  let className;
  let onMouseEnter;
  let onMouseLeave;
  let props;
  let t1;
  if ($[0] !== t0) {
    ({
      onMouseEnter,
      onMouseLeave,
      className,
      size: t1,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = className;
    $[2] = onMouseEnter;
    $[3] = onMouseLeave;
    $[4] = props;
    $[5] = t1;
  } else {
    className = $[1];
    onMouseEnter = $[2];
    onMouseLeave = $[3];
    props = $[4];
    t1 = $[5];
  }
  const size = t1 === undefined ? 24 : t1;
  const controls = useAnimation();
  const isControlledRef = useRef(false);
  let t2;
  if ($[6] !== controls) {
    t2 = () => {
      isControlledRef.current = true;
      return {
        startAnimation: () => controls.start("animate"),
        stopAnimation: () => controls.start("normal")
      };
    };
    $[6] = controls;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  useImperativeHandle(ref, t2);
  let t3;
  if ($[8] !== controls || $[9] !== onMouseEnter) {
    t3 = e => {
      if (!isControlledRef.current) {
        controls.start("animate");
      }
      onMouseEnter?.(e);
    };
    $[8] = controls;
    $[9] = onMouseEnter;
    $[10] = t3;
  } else {
    t3 = $[10];
  }
  const handleMouseEnter = t3;
  let t4;
  if ($[11] !== controls || $[12] !== onMouseLeave) {
    t4 = e_0 => {
      if (!isControlledRef.current) {
        controls.start("normal");
      }
      onMouseLeave?.(e_0);
    };
    $[11] = controls;
    $[12] = onMouseLeave;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  const handleMouseLeave = t4;
  let t5;
  if ($[14] !== className) {
    t5 = cn("inline-flex items-center justify-center", className);
    $[14] = className;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  let t6;
  if ($[16] !== size) {
    t6 = {
      width: size,
      height: size
    };
    $[16] = size;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t10;
  let t11;
  let t7;
  let t8;
  let t9;
  if ($[18] !== controls) {
    t7 = <m.path d="M9.937 15.5A2 2 0 0 0 8.5 14.063l-6.135-1.582a.5.5 0 0 1 0-.962L8.5 9.936A2 2 0 0 0 9.937 8.5l1.582-6.135a.5.5 0 0 1 .963 0L14.063 8.5A2 2 0 0 0 15.5 9.937l6.135 1.581a.5.5 0 0 1 0 .964L15.5 14.063a2 2 0 0 0-1.437 1.437l-1.582 6.135a.5.5 0 0 1-.963 0z" variants={SPARKLE_MAIN_VARIANTS} initial="normal" animate={controls} />;
    t8 = <m.path d="M20 3v4" variants={SPARKLE_SMALL1_VARIANTS} initial="normal" animate={controls} />;
    t9 = <m.path d="M22 5h-4" variants={SPARKLE_SMALL1_VARIANTS} initial="normal" animate={controls} />;
    t10 = <m.path d="M4 17v2" variants={SPARKLE_SMALL2_VARIANTS} initial="normal" animate={controls} />;
    t11 = <m.path d="M5 18H3" variants={SPARKLE_SMALL2_VARIANTS} initial="normal" animate={controls} />;
    $[18] = controls;
    $[19] = t10;
    $[20] = t11;
    $[21] = t7;
    $[22] = t8;
    $[23] = t9;
  } else {
    t10 = $[19];
    t11 = $[20];
    t7 = $[21];
    t8 = $[22];
    t9 = $[23];
  }
  let t12;
  if ($[24] !== controls || $[25] !== size || $[26] !== t10 || $[27] !== t11 || $[28] !== t7 || $[29] !== t8 || $[30] !== t9) {
    t12 = <m.svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" variants={SVG_VARIANTS} initial="normal" animate={controls}>{t7}{t8}{t9}{t10}{t11}</m.svg>;
    $[24] = controls;
    $[25] = size;
    $[26] = t10;
    $[27] = t11;
    $[28] = t7;
    $[29] = t8;
    $[30] = t9;
    $[31] = t12;
  } else {
    t12 = $[31];
  }
  let t13;
  if ($[32] !== handleMouseEnter || $[33] !== handleMouseLeave || $[34] !== props || $[35] !== t12 || $[36] !== t5 || $[37] !== t6) {
    t13 = <div className={t5} style={t6} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} {...props}>{t12}</div>;
    $[32] = handleMouseEnter;
    $[33] = handleMouseLeave;
    $[34] = props;
    $[35] = t12;
    $[36] = t5;
    $[37] = t6;
    $[38] = t13;
  } else {
    t13 = $[38];
  }
  return t13;
});
SparklesIcon.displayName = "SparklesIcon";
export { SparklesIcon };